import { pipelineBuckets, PIPELINE_STAGES } from '@/utils/jobStatus';

const STAGE_KEYS = ['saved', 'ready', 'applied', 'interviewing', 'offer'];

const NODE_COLORS = {
  saved: '#64748b',
  ready: '#667eea',
  applied: '#818cf8',
  interviewing: '#22c55e',
  offer: '#16a34a',
  passed: '#f87171',
};

function nodeName(key, count) {
  const meta = PIPELINE_STAGES.find((s) => s.key === key);
  const label = key === 'passed' ? 'Passed' : meta?.label || key;
  return `${label} (${count})`;
}

/** Build ECharts sankey series data + links from precomputed buckets. */
export function buildPipelineSankeyDataFromBuckets(b = {}) {

  const nodeKeys = [...STAGE_KEYS];
  if (b.passed > 0) nodeKeys.push('passed');

  const data = nodeKeys.map((key) => ({
    name: nodeName(key, b[key] || 0),
    itemStyle: { color: NODE_COLORS[key] || '#94a3b8' },
  }));

  const nameFor = (key) => nodeName(key, b[key] || 0);

  const links = [];

  const withResume =
    (b.ready || 0) +
    (b.applied || 0) +
    (b.interviewing || 0) +
    (b.offer || 0) +
    (b.passed || 0);
  const pastReady =
    (b.applied || 0) + (b.interviewing || 0) + (b.offer || 0) + (b.passed || 0);
  const pastInterview = (b.interviewing || 0) + (b.offer || 0);

  const progressStyle = { color: '#22c55e', opacity: 0.55 };
  const negativeStyle = { color: '#f87171', opacity: 0.55 };

  if (withResume > 0) {
    links.push({
      source: nameFor('saved'),
      target: nameFor('ready'),
      value: withResume,
      lineStyle: progressStyle,
    });
  }
  if (pastReady > 0) {
    links.push({
      source: nameFor('ready'),
      target: nameFor('applied'),
      value: pastReady,
      lineStyle: progressStyle,
    });
  }
  if (pastInterview > 0) {
    links.push({
      source: nameFor('applied'),
      target: nameFor('interviewing'),
      value: pastInterview,
      lineStyle: progressStyle,
    });
  }
  if ((b.offer || 0) > 0) {
    links.push({
      source: nameFor('interviewing'),
      target: nameFor('offer'),
      value: b.offer,
      lineStyle: progressStyle,
    });
  }
  if (b.passed > 0) {
    links.push({
      source: nameFor('applied'),
      target: nameFor('passed'),
      value: b.passed,
      lineStyle: negativeStyle,
    });
  }

  return { data, links };
}

/** Build sankey data from a jobs array (client-side) or pass buckets via buildPipelineSankeyDataFromBuckets. */
export function buildPipelineSankeyData(jobs = []) {
  return buildPipelineSankeyDataFromBuckets(pipelineBuckets(jobs));
}

export function buildPipelineSankeyOption(jobsOrBuckets = [], { isDark = false } = {}) {
  const { data, links } = Array.isArray(jobsOrBuckets)
    ? buildPipelineSankeyData(jobsOrBuckets)
    : buildPipelineSankeyDataFromBuckets(jobsOrBuckets || {});
  const textColor = isDark ? '#f1f5f9' : '#1a1a2e';
  const mutedColor = isDark ? '#94a3b8' : '#64748b';

  return {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'item',
      triggerOn: 'mousemove',
      backgroundColor: isDark ? '#1e293b' : '#ffffff',
      borderColor: isDark ? '#334155' : '#e2e8f0',
      textStyle: { color: textColor, fontSize: 13 },
      formatter(params) {
        if (params.dataType === 'edge') {
          return `${params.data.source} → ${params.data.target}<br/><strong>${params.data.value}</strong> jobs`;
        }
        return params.name;
      },
    },
    series: [
      {
        type: 'sankey',
        left: '4%',
        right: '14%',
        top: 24,
        bottom: 16,
        nodeWidth: 14,
        nodeGap: 14,
        layoutIterations: 32,
        emphasis: {
          focus: 'adjacency',
        },
        lineStyle: {
          curveness: 0.5,
        },
        label: {
          color: textColor,
          fontSize: 12,
          fontWeight: 500,
        },
        itemStyle: {
          borderWidth: 0,
        },
        data,
        links,
      },
    ],
    graphic: links.length
      ? []
      : [
          {
            type: 'text',
            left: 'center',
            top: 'middle',
            style: {
              text: 'No pipeline flow yet',
              fill: mutedColor,
              fontSize: 14,
            },
          },
        ],
  };
}
